import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  Search,
  ShieldCheck,
  ShieldAlert,
  Globe,
  Wallet as WalletIcon,
  DollarSign,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/buyers — Bank-grade admin-only directory of every fan/buyer.
 * Filter by country, confirmation status, and free-text search across
 * email/name/user_id. Clicking a row loads full transaction + ledger
 * history. Non-admins are blocked server-side (403).
 */
const AdminBuyers = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [confirmedFilter, setConfirmedFilter] = useState("all"); // all | confirmed | pending
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (country) params.set("country", country);
      if (search) params.set("search", search);
      if (confirmedFilter === "confirmed") params.set("address_confirmed", "true");
      else if (confirmedFilter === "pending") params.set("address_confirmed", "false");
      const { data } = await axios.get(`${API}/admin/buyers/list?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.buyers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load buyers");
    } finally {
      setLoading(false);
    }
  }, [token, search, country, confirmedFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (buyer) => {
    setSelected(buyer);
    setDetail(null);
    try {
      const { data } = await axios.get(`${API}/admin/buyers/${buyer.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load buyer");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-buyers-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center" data-testid="buyers-back-btn" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">Admin · Buyers / Fans</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#8DB87A]" />
            <span className="text-sm text-[#8A92A0]" data-testid="buyers-count">{rows.length} accounts</span>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Buyers / Fans directory</h1>
          <p className="text-[#8A92A0] max-w-3xl">
            Bank-grade admin-only list of every fan account. Full address, wallet balance, lifetime spend, and quorum verdict for every buyer. Data is 5/5 leak-proof — non-admin roles are blocked at the API layer.
          </p>
        </div>

        {/* Filter bar */}
        <div className="card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="buyers-filter-bar">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A92A0]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email, name, user_id…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm"
              data-testid="buyers-search-input"
            />
          </div>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
            placeholder="Country (US, CA, GB…)"
            maxLength={2}
            className="w-32 px-3 py-2 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm uppercase font-mono"
            data-testid="buyers-country-input"
          />
          <div className="flex items-center gap-1">
            {[
              { key: "all", label: "All" },
              { key: "confirmed", label: "Address confirmed" },
              { key: "pending", label: "Pending confirm" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setConfirmedFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  confirmedFilter === f.key
                    ? "bg-[#F0C464] text-black"
                    : "bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:text-white"
                }`}
                data-testid={`buyers-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F0C464]" /></div>
        ) : rows.length === 0 ? (
          <div className="card p-16 text-center" data-testid="buyers-empty">
            <Users className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">No buyers match your filters</h3>
          </div>
        ) : (
          <div className="card overflow-hidden" data-testid="buyers-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1A2030] text-left text-[10px] uppercase tracking-widest text-[#8A92A0]">
                    <th className="px-4 py-3">Name / Email</th>
                    <th className="px-4 py-3">Country</th>
                    <th className="px-4 py-3">Address</th>
                    <th className="px-4 py-3">KYC</th>
                    <th className="px-4 py-3 text-right">Wallet</th>
                    <th className="px-4 py-3 text-right">Lifetime spend</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.user_id} className="border-b border-[#1A2030]/50 hover:bg-[#0A0E18]" data-testid={`buyer-row-${b.user_id}`}>
                      <td className="px-4 py-3">
                        <p className="text-white font-semibold">{b.name || "—"}</p>
                        <p className="text-xs text-[#8A92A0] font-mono">{b.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs text-[#8A92A0]">
                          <Globe className="w-3 h-3" /> {(b.address || {}).country || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {b.address_confirmed ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#8DB87A]/20 text-[#8DB87A] text-[10px] font-bold uppercase tracking-widest">
                            <ShieldCheck className="w-3 h-3" /> Confirmed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#EBB84C]/20 text-[#EBB84C] text-[10px] font-bold uppercase tracking-widest">
                            <ShieldAlert className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-[#8A92A0]">{b.kyc_status || "not_started"}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-white">
                        {b.wallet_credits ? `${b.wallet_credits} Tokens` : "0 Tokens"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-white">
                        ${(b.lifetime_spend || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openDetail(b)}
                          className="text-[#F0C464] text-xs font-bold hover:underline flex items-center gap-1 ml-auto"
                          data-testid={`buyer-detail-btn-${b.user_id}`}
                        >
                          Open <ChevronRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" data-testid="buyer-detail-modal" onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="bg-[#0A0E18] border border-[#1A2030] rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-[#8A92A0] font-mono mb-1">{selected.user_id}</p>
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white">{selected.name || selected.email}</h2>
                <p className="text-sm text-[#8A92A0]">{selected.email}</p>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-[#8A92A0] hover:text-white text-xl leading-none">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg bg-[#050810] border border-[#1A2030] p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Wallet</p>
                <p className="text-white font-bold text-lg flex items-center gap-1"><WalletIcon className="w-4 h-4 text-[#F0C464]" /> {detail?.wallet_balance ?? "…"} cr</p>
              </div>
              <div className="rounded-lg bg-[#050810] border border-[#1A2030] p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Lifetime spend</p>
                <p className="text-white font-bold text-lg flex items-center gap-1"><DollarSign className="w-4 h-4 text-[#8DB87A]" /> ${(selected.lifetime_spend || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="rounded-lg bg-[#050810] border border-[#1A2030] p-3 mb-4">
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0] mb-2">Confirmed billing address</p>
              {selected.address && Object.keys(selected.address).length ? (
                <div className="text-sm text-white">
                  <p>{selected.address.address_line1 || "—"} {selected.address.address_line2 || ""}</p>
                  <p>{selected.address.city || "—"}, {selected.address.state || "—"} {selected.address.postal_code || ""}</p>
                  <p className="text-[#8A92A0]">{selected.address.country || "—"}</p>
                </div>
              ) : (
                <p className="text-xs text-[#8A92A0]">No address on file.</p>
              )}
            </div>

            {detail && (
              <>
                <h3 className="text-white font-bold mb-2 mt-4">Recent transactions ({detail.transactions?.length || 0})</h3>
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {(detail.transactions || []).slice(0, 20).map((t) => (
                    <div key={t.transaction_id} className="rounded bg-[#050810] border border-[#1A2030] p-2 text-xs flex items-center justify-between">
                      <div>
                        <p className="text-white font-mono">{t.transaction_id}</p>
                        <p className="text-[#8A92A0]">{t.item_type} · {t.payment_status} · {t.created_at}</p>
                      </div>
                      <p className="text-white font-bold">${(t.amount || 0).toFixed(2)}</p>
                    </div>
                  ))}
                  {(!detail.transactions || detail.transactions.length === 0) && (
                    <p className="text-xs text-[#4A5260] italic">No transactions.</p>
                  )}
                </div>
                <h3 className="text-white font-bold mb-2">Wallet ledger ({detail.ledger?.length || 0})</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(detail.ledger || []).slice(0, 20).map((l) => (
                    <div key={l.entry_id} className="rounded bg-[#050810] border border-[#1A2030] p-2 text-[11px] flex items-center justify-between">
                      <span className="text-[#8A92A0]">{l.reason} · {l.created_at}</span>
                      <span className={`font-mono ${(l.delta_cents || 0) >= 0 ? "text-[#8DB87A]" : "text-[#EC008C]"}`}>{(l.delta_cents / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBuyers;
